"use client";

import { tokenService, TokenRefreshError } from "../auth/TokenService";
import { signOut } from "firebase/auth";
import { isFirebaseInitialized } from "@/lib/firebase";
import { handleUnauthorizedResponse } from "@/infrastructure/auth/authUtils";
import { ACCOUNT_BLOCKED_CODE } from "@/lib/account-blocked";
import { auth } from "@/lib/firebase";
import { getOrCreateRequestId } from "./request-id";
import { localCustdevRequestUrl } from "./local-custdev-url";
import {
  canRetryByMethod,
  getRetryDelayMs,
  getTransportPolicy,
  isRetryableStatusCode,
  normalizeHttpMethod,
  sleep,
  type TransportRequestConfig,
} from "./transport-policy";
import {
  TransportError,
  isAbortError,
  isRetryableNetworkError,
} from "./transport-error";

interface PreparedRequestSignal {
  signal?: AbortSignal;
  wasTimeout: () => boolean;
  cleanup: () => void;
}

/**
 * HTTP client with token refresh, timeout, retry and idempotency support.
 */
export class HttpClient {
  private static instance: HttpClient;

  private constructor() {}

  public static getInstance(): HttpClient {
    if (!HttpClient.instance) {
      HttpClient.instance = new HttpClient();
    }
    return HttpClient.instance;
  }

  public async request(
    url: string,
    options: RequestInit = {},
    transportConfig: TransportRequestConfig = {}
  ): Promise<Response> {
    url = localCustdevRequestUrl(
      url,
      typeof window === "undefined" ? undefined : window.location.href
    );
    const method = normalizeHttpMethod(options.method);
    const requestId = getOrCreateRequestId(
      transportConfig.requestId,
      "custdev-http"
    );
    const idempotencyKey = transportConfig.idempotencyKey;

    const policy = getTransportPolicy("client", transportConfig);
    const canRetry = canRetryByMethod(method, idempotencyKey);
    const maxAttempts = canRetry ? Math.max(1, policy.maxAttempts) : 1;

    let lastError: unknown = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const headers = await this.buildHeaders(options.headers, method, options.body);
      headers.set("X-Request-ID", requestId);
      headers.set("X-Retry-Attempt", String(attempt));

      if (idempotencyKey) {
        headers.set("Idempotency-Key", idempotencyKey);
      }

      const preparedSignal = this.prepareSignal(options.signal, policy.timeoutMs);

      try {
        const requestOptions: RequestInit = {
          ...options,
          headers,
          signal: preparedSignal.signal,
        };

        let response = await fetch(url, requestOptions);

        // A provider credential rejection is distinct from the user's Firebase
        // session. Refreshing/signing out cannot repair the server's BYOK key.
        let providerCredentialRejection = false;
        if (response.status === 401 && url.includes("/admin/openrouter/")) {
          const problem = await response.clone().json().catch(() => null);
          providerCredentialRejection = problem?.detail?.code === "credentials_invalid";
        }
        if (response.status === 401 && !providerCredentialRejection) {
          response = await this.tryRefreshTokenAndRetry(
            url,
            options,
            method,
            requestId,
            idempotencyKey,
            preparedSignal.signal
          );
        }

        if (response.status === 403 && (await this.isBlockedResponse(response))) {
          if (auth) {
            void signOut(auth);
          }
          handleUnauthorizedResponse("blocked");
        }

        if (
          !response.ok &&
          this.shouldRetryStatus(response.status, attempt, maxAttempts)
        ) {
          const retryDelayMs = getRetryDelayMs(attempt, policy);
          await sleep(retryDelayMs);
          continue;
        }

        return response;
      } catch (error) {
        lastError = error;

        if (isAbortError(error) && options.signal?.aborted && !preparedSignal.wasTimeout()) {
          throw new TransportError("Request aborted", {
            code: "REQUEST_ABORTED",
            retryable: false,
            requestId,
            cause: error,
          });
        }

        if (
          this.shouldRetryError(error, preparedSignal.wasTimeout(), attempt, maxAttempts)
        ) {
          const retryDelayMs = getRetryDelayMs(attempt, policy);
          await sleep(retryDelayMs);
          continue;
        }

        if (preparedSignal.wasTimeout()) {
          throw new TransportError(
            `Request timeout after ${policy.timeoutMs}ms`,
            {
              code: "TIMEOUT",
              retryable: false,
              requestId,
              cause: error,
            }
          );
        }

        if (isRetryableNetworkError(error)) {
          throw new TransportError("Network request failed", {
            code: "NETWORK_ERROR",
            retryable: false,
            requestId,
            cause: error,
          });
        }

        throw error;
      } finally {
        preparedSignal.cleanup();
      }
    }

    if (lastError instanceof Error) {
      throw lastError;
    }

    throw new TransportError("HTTP request failed", {
      code: "UNKNOWN",
      retryable: false,
      requestId,
      cause: lastError,
    });
  }

  private async isBlockedResponse(response: Response): Promise<boolean> {
    try {
      const data = (await response.clone().json()) as {
        code?: string;
        detail?: { code?: string };
      };
      return (
        data.code === ACCOUNT_BLOCKED_CODE ||
        data.detail?.code === ACCOUNT_BLOCKED_CODE
      );
    } catch {
      return false;
    }
  }

  private async tryRefreshTokenAndRetry(
    url: string,
    options: RequestInit,
    method: string,
    requestId: string,
    idempotencyKey: string | undefined,
    signal?: AbortSignal
  ): Promise<Response> {
    try {
      await tokenService.refreshToken();

      const newHeaders = await this.buildHeaders(options.headers, method, options.body);
      newHeaders.set("X-Request-ID", requestId);
      newHeaders.set("X-Retry-Attempt", "auth-refresh");
      if (idempotencyKey) {
        newHeaders.set("Idempotency-Key", idempotencyKey);
      }

      const retryOptions: RequestInit = {
        ...options,
        headers: newHeaders,
        signal,
      };

      const retryResponse = await fetch(url, retryOptions);
      if (retryResponse.status === 401) {
        handleUnauthorizedResponse("expired");
      }

      return retryResponse;
    } catch (error) {
      if (error instanceof TokenRefreshError && error.isExpired) {
        console.warn("Detected expired Firebase token");
        handleUnauthorizedResponse("expired");
      } else {
        console.error("Token refresh failed:", error);
        handleUnauthorizedResponse("invalid");
      }

      return new Response(null, {
        status: 401,
        statusText: "Unauthorized",
      });
    }
  }

  private shouldRetryStatus(
    statusCode: number,
    attempt: number,
    maxAttempts: number
  ): boolean {
    if (attempt >= maxAttempts) {
      return false;
    }

    return isRetryableStatusCode(statusCode);
  }

  private shouldRetryError(
    error: unknown,
    timedOut: boolean,
    attempt: number,
    maxAttempts: number
  ): boolean {
    if (attempt >= maxAttempts) {
      return false;
    }

    if (timedOut) {
      return true;
    }

    return isRetryableNetworkError(error);
  }

  private prepareSignal(
    sourceSignal: AbortSignal | null | undefined,
    timeoutMs: number
  ): PreparedRequestSignal {
    const shouldUseTimeout = Number.isFinite(timeoutMs) && timeoutMs > 0;

    if (!sourceSignal && !shouldUseTimeout) {
      return {
        signal: undefined,
        wasTimeout: () => false,
        cleanup: () => undefined,
      };
    }

    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let timedOut = false;

    const abortFromSource = () => {
      controller.abort(sourceSignal?.reason);
    };

    if (sourceSignal) {
      if (sourceSignal.aborted) {
        controller.abort(sourceSignal.reason);
      } else {
        sourceSignal.addEventListener("abort", abortFromSource, { once: true });
      }
    }

    if (shouldUseTimeout) {
      timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs);
    }

    return {
      signal: controller.signal,
      wasTimeout: () => timedOut,
      cleanup: () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (sourceSignal) {
          sourceSignal.removeEventListener("abort", abortFromSource);
        }
      },
    };
  }

  private async buildHeaders(
    existingHeaders?: HeadersInit,
    method: string = "GET",
    body?: BodyInit | null
  ): Promise<Headers> {
    const headers = new Headers(existingHeaders);

    const normalizedMethod = method.toUpperCase();
    const hasManagedBody =
      body instanceof FormData ||
      body instanceof URLSearchParams ||
      body instanceof Blob ||
      body instanceof ArrayBuffer ||
      ArrayBuffer.isView(body ?? null);

    const shouldAttachJsonContentType =
      !headers.has("Content-Type") &&
      normalizedMethod !== "GET" &&
      normalizedMethod !== "HEAD" &&
      !hasManagedBody;

    if (shouldAttachJsonContentType) {
      headers.set("Content-Type", "application/json");
    }

    if (isFirebaseInitialized() && tokenService.isAuthenticated()) {
      try {
        const authHeaders = await tokenService.getAuthHeaders();
        Object.entries(authHeaders).forEach(([key, value]) => {
          headers.set(key, value);
        });
      } catch (error) {
        console.warn("Failed to add auth headers:", error);
      }
    }

    return headers;
  }

  public async get(
    url: string,
    options: RequestInit = {},
    transportConfig: TransportRequestConfig = {}
  ): Promise<Response> {
    return this.request(url, { ...options, method: "GET" }, transportConfig);
  }

  public async post(
    url: string,
    body?: unknown,
    options: RequestInit = {},
    transportConfig: TransportRequestConfig = {}
  ): Promise<Response> {
    const requestOptions: RequestInit = {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    if (body !== undefined) {
      try {
        requestOptions.body = JSON.stringify(body);
      } catch (error) {
        throw new Error(
          `Failed to stringify request body: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    return this.request(url, requestOptions, transportConfig);
  }

  public async put(
    url: string,
    body?: unknown,
    options: RequestInit = {},
    transportConfig: TransportRequestConfig = {}
  ): Promise<Response> {
    const requestOptions: RequestInit = {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    if (body !== undefined) {
      try {
        requestOptions.body = JSON.stringify(body);
      } catch (error) {
        throw new Error(
          `Failed to stringify request body: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    return this.request(url, requestOptions, transportConfig);
  }

  public async patch(
    url: string,
    body?: unknown,
    options: RequestInit = {},
    transportConfig: TransportRequestConfig = {}
  ): Promise<Response> {
    const requestOptions: RequestInit = {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    if (body !== undefined) {
      try {
        requestOptions.body = JSON.stringify(body);
      } catch (error) {
        throw new Error(
          `Failed to stringify request body: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    return this.request(url, requestOptions, transportConfig);
  }

  public async delete(
    url: string,
    options: RequestInit = {},
    transportConfig: TransportRequestConfig = {}
  ): Promise<Response> {
    return this.request(url, { ...options, method: "DELETE" }, transportConfig);
  }
}

export const httpClient = HttpClient.getInstance();
export type { TransportRequestConfig };
